import { useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { SelectInput } from '@interxfi/design-system/components/input/select-input/select-input';
import type { InputDropdownOption } from '@interxfi/design-system/components/input/shared/dropdown/input-dropdown';
import { itinMask, ssnMask } from '@interxfi/design-system/components/input/text-input/masks';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { type I18nNamespaces, type TFunction, useTranslation } from '@interxfi/translation';
import type { IxcRegistrationV1IndividualTaxIdentifier, IxcRegistrationV1IndividualTaxIdentifierOneOfValue } from '@interxfi/types/generated/api';
import { AppPasswordInputWithLens } from '../../../components/form/app-password-input';
import { ITIN_PATTERN, SSN_PATTERN } from '../shared-utils/tax-id';

export const ssnFormSchema = z.object({
  type: z.literal('ssn'),
  value: z.string().regex(SSN_PATTERN, 'SSN must be in the format XXX-XX-XXXX'),
});

export const itinFormSchema = z.object({
  type: z.literal('itin'),
  value: z.string().regex(ITIN_PATTERN, 'ITIN must be in the format 9XX-XX-XXXX'),
});

export const individualTaxIdFormSchema = z.discriminatedUnion('type', [ssnFormSchema, itinFormSchema]);

export type IndividualTaxIdFormValues = z.infer<typeof individualTaxIdFormSchema>;

const defaultIndividualTaxIdFormValuesByType: Record<string, IndividualTaxIdFormValues> = {
  ssn: {
    type: 'ssn',
    value: '',
  },
  itin: {
    type: 'itin',
    value: '',
  },
} as const;

export const defaultIndividualTaxIdValues: IndividualTaxIdFormValues = defaultIndividualTaxIdFormValuesByType.ssn;

export function getInitialFormValuesForIndividualTaxId(
  taxId: IxcRegistrationV1IndividualTaxIdentifier | undefined,
): IndividualTaxIdFormValues | undefined {
  if (!taxId) {
    return undefined;
  }

  if ('ssn' in taxId) {
    return { type: 'ssn', value: taxId.ssn?.number ?? '' };
  }

  if ('itin' in taxId) {
    return { type: 'itin', value: taxId.itin?.number ?? '' };
  }

  return undefined;
}

function getTaxIdTypeOptions(t: TFunction): InputDropdownOption<IxcRegistrationV1IndividualTaxIdentifierOneOfValue>[] {
  return [
    { label: t('ixcRegistrationV1:oneOf.IxcRegistrationV1IndividualTaxIdentifier.ssn'), value: 'ssn' },
    { label: t('ixcRegistrationV1:oneOf.IxcRegistrationV1IndividualTaxIdentifier.itin'), value: 'itin' },
  ];
}

export function formValuesToIndividualTaxIdentifier(taxIdentifier: IndividualTaxIdFormValues): IxcRegistrationV1IndividualTaxIdentifier {
  return match(taxIdentifier)
    .returnType<IxcRegistrationV1IndividualTaxIdentifier>()
    .with({ type: 'itin' }, ({ value }) => ({ itin: { number: value } }))
    .with({ type: 'ssn' }, ({ value }) => ({ ssn: { number: value } }))
    .exhaustive();
}

const DEFAULT_INDIVIDUAL_TAX_ID_COLUMNS: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

interface IndividualTaxIdFormProps {
  columns?: ResponsiveValue<number>;
  lens: Lens<IndividualTaxIdFormValues>;
}

const i18nNamespaces: I18nNamespaces[] = ['ixcRegistrationV1'] as const;

export function IndividualTaxIdForm({ columns = DEFAULT_INDIVIDUAL_TAX_ID_COLUMNS, lens }: IndividualTaxIdFormProps) {
  const { t } = useTranslation(i18nNamespaces);
  const { setValue } = useFormContext();
  const { name } = lens.interop();
  const typeLensInterop = lens.focus('type').interop();
  const typeValue = useWatch({ ...typeLensInterop, defaultValue: 'ssn' });
  const mask = typeValue === 'itin' ? itinMask : ssnMask;
  const typeOptions = useMemo(() => getTaxIdTypeOptions(t), [t]);

  return (
    <Grid autoColumns={columns} gap={4}>
      <SelectInput
        label="Tax Identifier"
        name={typeLensInterop.name}
        onChange={(e) => {
          setValue(
            name,
            { ...defaultIndividualTaxIdFormValuesByType[e.target.value as IxcRegistrationV1IndividualTaxIdentifierOneOfValue] },
            {
              shouldValidate: true,
            },
          );
        }}
        options={typeOptions}
        required
        value={typeValue}
      />

      <AppPasswordInputWithLens
        autoComplete="off"
        label={t(`ixcRegistrationV1:oneOf.IxcRegistrationV1IndividualTaxIdentifier.${typeValue}`)}
        lens={lens.focus('value')}
        maskOptions={mask}
        required
      />
    </Grid>
  );
}
